import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { C, FilterSelect, Notice, Panel } from '@/design-system/dna';
import { brl } from '../shared';

const Row = ({ label, value, strong, tone }) => (
  <div className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: C.line }}>
    <span className={strong ? 'font-bold' : ''}>{label}</span><span className={`tabular-nums ${strong ? 'text-lg font-extrabold' : 'font-semibold'}`} style={tone ? { color: tone } : undefined}>{value}</span>
  </div>
);

function Consolidado({ p }) {
  return (
    <div data-testid="evento-caixa-previa">
      <Row label="Receitas (bruto)" value={brl(p.receitas)} tone={C.green} />
      <Row label="(−) Taxas financeiras" value={brl(p.taxas)} tone={C.amber} />
      <Row label="(−) Custos" value={brl(p.custos)} tone={C.orange} />
      <Row label="Resultado líquido" value={brl(p.resultado)} strong tone={p.resultado >= 0 ? C.green : C.magenta} />
    </div>
  );
}

export function Financeiro({ data, eventId, reload, active }) {
  const [prev, setPrev] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [acc, setAcc] = useState('');
  const [posting, setPosting] = useState(false);
  const load = useCallback(() => api.get(`/eventos/${eventId}/caixa/previa`).then(({ data: d }) => setPrev(d)).catch(() => setPrev(null)), [eventId]);
  useEffect(() => { if (active) load(); }, [active, load, data]);
  useEffect(() => { api.get('/financial/accounts/receiving').then(({ data: d }) => { setAccounts(d.accounts); setAcc(d.accounts[0]?.id || ''); }).catch(() => {}); }, []);
  const post = async () => {
    setPosting(true);
    try {
      const { data: d } = await api.post(`/eventos/${eventId}/caixa/lancar`, { expense_account_id: acc || null });
      toast.success(d.already ? 'Nada novo a lançar — evento já integrado.' : `${d.created} lançamento(s) criado(s) no Financeiro.`);
      setPrev(d.preview); setConfirm(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao lançar.'); } finally { setPosting(false); }
  };
  if (!prev) return <p className="flex items-center gap-2 py-10 text-sm" style={{ color: C.muted }}><Loader2 className="h-4 w-4 animate-spin" /> Consolidando…</p>;
  const pend = prev.pendente;
  const hasPending = pend.pagamentos > 0 || pend.linhas_custo.length > 0;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Resultado consolidado" kicker="Financeiro do evento" testid="evento-financeiro">
        <Consolidado p={prev} />
        {prev.custos_alterados.length > 0 && <div className="mt-3"><Notice tone="warning" testid="evento-custos-reduzidos">Custo reduzido após o lançamento ({prev.custos_alterados.map((c) => c.key).join(', ')}). Ajuste o excedente pela Central de Lançamentos.</Notice></div>}
      </Panel>
      <Panel title="Integração com o Caixa" kicker="Caixa · DRE · Lançamentos" testid="evento-integracao"
        right={<Button onClick={() => setConfirm(true)} disabled={!hasPending} data-testid="evento-lancar-caixa"><Landmark className="h-4 w-4" /> Lançar evento no Caixa</Button>}>
        {prev.integrado ? (
          <div className="space-y-3" data-testid="evento-integrado">
            <Notice tone="success" title="Evento integrado ao Financeiro">Receitas, taxas e custos lançados no razão oficial com o código do evento. Cada novo recebimento já entra no Caixa/DRE/Lançamentos no ato; novos custos aparecem aqui como pendentes.</Notice>
            <Button asChild variant="outline"><Link to="/v2/financeiro/lancamentos" data-testid="evento-ver-lancamentos"><ExternalLink className="h-4 w-4" /> Ver lançamentos</Link></Button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p style={{ color: C.muted }}>Pendente de lançamento:</p>
            <Row label={`${pend.pagamentos} recebimento(s)`} value={brl(pend.receitas)} />
            <Row label="Taxas desses recebimentos" value={brl(pend.taxas)} />
            <Row label={`${pend.linhas_custo.length} linha(s) de custo`} value={brl(pend.custos)} />
            {prev.integration && <p className="pt-2 text-[12px] inline-flex items-center gap-1" style={{ color: C.green }}><CheckCircle2 className="h-3.5 w-3.5" /> Lançamento anterior em {new Date(prev.integration.posted_at).toLocaleString('pt-BR')} — só a diferença será lançada.</p>}
          </div>
        )}
      </Panel>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-md" data-testid="evento-caixa-dialog">
          <DialogHeader><DialogTitle>Lançar evento no Caixa</DialogTitle><DialogDescription>Prévia consolidada. Lançamento idempotente: nada é lançado duas vezes.</DialogDescription></DialogHeader>
          <Consolidado p={prev} />
          <div className="rounded-2xl p-3 text-[12.5px]" style={{ background: C.paper }}>
            Será lançado agora: <b>{brl(pend.receitas)}</b> em receitas (conta de recebimento de cada pagamento), <b>{brl(pend.taxas)}</b> em taxas (dedução) e <b>{brl(pend.custos)}</b> em despesas.
          </div>
          {pend.linhas_custo.length > 0 && <label className="text-[12px] font-semibold">Conta de saída das despesas<FilterSelect id="evento-conta-despesa" value={acc} onChange={setAcc} items={accounts.map((a) => ({ value: a.id, label: a.nome }))} /></label>}
          <DialogFooter><Button variant="outline" onClick={() => setConfirm(false)}>Cancelar</Button><Button onClick={post} disabled={posting} data-testid="evento-caixa-confirmar">{posting && <Loader2 className="h-4 w-4 animate-spin" />} Confirmar lançamento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
